// UltraFlex Admin Portal - Interactive Reports with Drill-Down
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, CreditCard,
  Download, Calendar, Building2, RefreshCw, FileText, Activity,
  ChevronDown, ChevronRight, Search, X, Eye, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReports = () => {
  const { token } = useAuth();
  const [activeReport, setActiveReport] = useState('membership');
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [siteComparison, setSiteComparison] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [cancellationData, setCancellationData] = useState(null);
  const [failedPaymentsData, setFailedPaymentsData] = useState(null);
  const [dayPassData, setDayPassData] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers }).then(r => r.json()).then(d => setSites(d.items || [])).catch(() => {});
  }, [token]);

  useEffect(() => { fetchReportData(); }, [activeReport, selectedSite, dateRange, auditPage]);

  const fetchReportData = async () => {
    setLoading(true);
    const params = new URLSearchParams({ from_date: dateRange.from, to_date: dateRange.to });
    if (selectedSite) params.append('site_id', selectedSite);
    try {
      if (activeReport === 'membership') {
        const res = await fetch(`${API_URL}/api/reporting/membership?${params}`, { headers });
        setReportData(await res.json());
      } else if (activeReport === 'revenue') {
        const res = await fetch(`${API_URL}/api/reporting/revenue?${params}`, { headers });
        setReportData(await res.json());
      } else if (activeReport === 'comparison') {
        const res = await fetch(`${API_URL}/api/reporting/site-comparison?${params}`, { headers });
        setSiteComparison(await res.json());
      } else if (activeReport === 'cancellations') {
        const res = await fetch(`${API_URL}/api/reporting/cancellation-cohort?${params}`, { headers });
        setCancellationData(await res.json());
      } else if (activeReport === 'failed_payments') {
        const res = await fetch(`${API_URL}/api/reporting/failed-payments?${params}`, { headers });
        setFailedPaymentsData(await res.json());
      } else if (activeReport === 'day_passes') {
        const res = await fetch(`${API_URL}/api/reporting/day-passes?${params}`, { headers });
        setDayPassData(await res.json());
      } else if (activeReport === 'audit') {
        const auditParams = new URLSearchParams({ page: auditPage.toString(), page_size: '30' });
        const res = await fetch(`${API_URL}/api/reporting/audit-log?${auditParams}`, { headers });
        const d = await res.json();
        setAuditLogs(d.items || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSite) params.append('site_id', selectedSite);
      const res = await fetch(`${API_URL}/api/reporting/export/members?${params}`, { headers });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `members_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { alert('Export failed'); }
  };

  const reportTypes = [
    { id: 'membership', label: 'Membership', icon: Users },
    { id: 'revenue', label: 'Revenue', icon: CreditCard },
    { id: 'comparison', label: 'Site Comparison', icon: Building2 },
    { id: 'cancellations', label: 'Cancellations', icon: TrendingDown },
    { id: 'failed_payments', label: 'Failed Payments', icon: Activity },
    { id: 'day_passes', label: 'Day Passes', icon: Calendar },
    { id: 'audit', label: 'Audit Log', icon: FileText }
  ];

  return (
    <div className="p-4 lg:p-8" data-testid="admin-reports-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text">Reports & Analytics</h1>
          <p className="text-uf-text-muted text-sm mt-1">Interactive drill-down reports</p>
        </div>
        <button onClick={exportCSV} className="flex items-center px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm hover:bg-uf-red hover:text-white transition-colors">
          <Download className="w-4 h-4 mr-2" />Export CSV
        </button>
      </div>

      {/* Report Tabs */}
      <div className="flex gap-1 mb-4 bg-uf-surface rounded-lg p-1 overflow-x-auto">
        {reportTypes.map(r => (
          <button key={r.id} onClick={() => { setActiveReport(r.id); setDrillDown(null); }} data-testid={`report-tab-${r.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeReport === r.id ? 'bg-uf-red text-white' : 'text-uf-text-muted hover:text-uf-text'}`}>
            <r.icon className="w-4 h-4" />{r.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeReport !== 'audit' && (
        <div className="flex flex-wrap gap-3 mb-6">
          <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="report-site-filter">
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={dateRange.from} onChange={e => setDateRange({...dateRange, from: e.target.value})} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
          <input type="date" value={dateRange.to} onChange={e => setDateRange({...dateRange, to: e.target.value})} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
          <button onClick={fetchReportData} className="flex items-center gap-1 bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm hover:bg-uf-surface-hi">
            <RefreshCw className="w-3 h-3" />Refresh
          </button>
        </div>
      )}

      {loading ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" /></div>
      ) : (
        <>
          {/* Drill-down back button */}
          {drillDown && (
            <div className="mb-4 flex items-center gap-2">
              <button onClick={() => setDrillDown(null)} className="flex items-center gap-1 text-uf-red text-sm hover:underline" data-testid="drill-back-btn">
                <ArrowLeft className="w-4 h-4" />Back to overview
              </button>
              <span className="text-uf-text-muted text-sm">Drill-down: {drillDown.label}</span>
            </div>
          )}

          {activeReport === 'membership' && <MembershipReport data={reportData} />}
          {activeReport === 'revenue' && <RevenueReport data={reportData} />}
          {activeReport === 'comparison' && <SiteComparisonReport data={siteComparison} />}
          {activeReport === 'cancellations' && <CancellationReport data={cancellationData} drillDown={drillDown} setDrillDown={setDrillDown} />}
          {activeReport === 'failed_payments' && <FailedPaymentsReport data={failedPaymentsData} drillDown={drillDown} setDrillDown={setDrillDown} />}
          {activeReport === 'day_passes' && <DayPassReport data={dayPassData} drillDown={drillDown} setDrillDown={setDrillDown} />}
          {activeReport === 'audit' && <AuditLogReport logs={auditLogs} page={auditPage} setPage={setAuditPage} />}
        </>
      )}
    </div>
  );
};

// ==================== MEMBERSHIP ====================
const MembershipReport = ({ data }) => {
  if (!data) return <Empty />;
  return (
    <div className="space-y-4" data-testid="membership-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Members" value={data.total_members} color="text-emerald-400" />
        <Stat label="Active" value={data.active_members} color="text-blue-400" />
        <Stat label="New (Period)" value={data.new_members} color="text-cyan-400" />
        <Stat label="Cancelled" value={data.cancelled_members} color="text-red-400" />
      </div>
      {data.by_plan && Object.keys(data.by_plan).length > 0 && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
          <h3 className="text-sm font-semibold text-uf-text mb-3">By Plan</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(data.by_plan).map(([plan, count]) => (
              <div key={plan} className="bg-uf-surface-hi rounded-lg p-3 text-center"><p className="text-xl font-bold text-uf-text">{count}</p><p className="text-uf-text-muted text-xs">{plan}</p></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== REVENUE ====================
const RevenueReport = ({ data }) => {
  if (!data) return <Empty />;
  return (
    <div className="space-y-4" data-testid="revenue-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Revenue" value={`£${(data.total_revenue || 0).toLocaleString()}`} color="text-emerald-400" />
        <Stat label="Membership" value={`£${(data.membership_revenue || 0).toLocaleString()}`} color="text-blue-400" />
        <Stat label="Day Passes" value={`£${(data.day_pass_revenue || 0).toLocaleString()}`} color="text-violet-400" />
        <Stat label="Shop" value={`£${(data.shop_revenue || 0).toLocaleString()}`} color="text-amber-400" />
      </div>
    </div>
  );
};

// ==================== SITE COMPARISON ====================
const SiteComparisonReport = ({ data }) => {
  if (!data?.length) return <Empty />;
  return (
    <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="comparison-report">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-uf-border bg-uf-surface-hi text-left text-uf-text-muted">
          <th className="p-3">Site</th><th className="p-3 text-right">Members</th><th className="p-3 text-right">Active</th><th className="p-3 text-right">New</th><th className="p-3 text-right">Cancelled</th>
        </tr></thead>
        <tbody>{data.map(s => (
          <tr key={s.site_id || s.site_name} className="border-b border-uf-border/50 hover:bg-uf-surface-hi">
            <td className="p-3 text-uf-text font-medium">{s.site_name}</td>
            <td className="p-3 text-right text-uf-text">{s.total_members}</td>
            <td className="p-3 text-right text-emerald-400">{s.active_members}</td>
            <td className="p-3 text-right text-blue-400">{s.new_members}</td>
            <td className="p-3 text-right text-red-400">{s.cancelled_members}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

// ==================== CANCELLATIONS (with drill-down) ====================
const CancellationReport = ({ data, drillDown, setDrillDown }) => {
  if (!data) return <Empty />;

  if (drillDown?.type === 'reason') {
    const records = (data.detail_records || []).filter(r => (r.reason || 'Not specified') === drillDown.value);
    return <DrillTable title={`Cancellations: ${drillDown.value}`} records={records} columns={['member_name','email','plan_name','tenure_days','cancelled_at']} />;
  }
  if (drillDown?.type === 'tenure') {
    return <DrillTable title={`Tenure: ${drillDown.label}`} records={data.detail_records || []} columns={['member_name','email','plan_name','reason','tenure_days','cancelled_at']} />;
  }

  return (
    <div className="space-y-4" data-testid="cancellation-report">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Total Cancellations" value={data.total_cancellations} color="text-red-400" />
        <Stat label="Top Reason" value={Object.entries(data.reasons || {}).sort((a,b) => b[1]-a[1])[0]?.[0] || 'N/A'} color="text-uf-text" small />
        <Stat label="Highest Tenure" value={Object.entries(data.tenure_breakdown || {}).sort((a,b) => b[1]-a[1])[0]?.[0]?.replace(/_/g, ' ') || 'N/A'} color="text-uf-text" small />
      </div>
      {/* Reasons - clickable */}
      <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
        <h3 className="text-sm font-semibold text-uf-text mb-3">Cancellation Reasons <span className="text-uf-text-muted text-xs font-normal">(click to drill down)</span></h3>
        <div className="space-y-2">
          {Object.entries(data.reasons || {}).sort((a,b) => b[1]-a[1]).map(([reason, count]) => {
            const pct = Math.round((count / Math.max(data.total_cancellations, 1)) * 100);
            return (
              <div key={reason} className="cursor-pointer hover:bg-uf-surface-hi rounded-lg p-2 transition-colors" data-testid={`reason-drill-${reason}`}
                onClick={() => setDrillDown({ type: 'reason', value: reason, label: `Reason: ${reason}` })}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-uf-text flex items-center gap-1">{reason} <ChevronRight className="w-3 h-3 text-uf-text-muted" /></span>
                  <span className="text-uf-text-muted">{count} ({pct}%)</span>
                </div>
                <div className="h-2 bg-uf-surface-hi rounded-full"><div className="h-full bg-red-500 rounded-full" style={{width: `${pct}%`}} /></div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Tenure - clickable */}
      <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
        <h3 className="text-sm font-semibold text-uf-text mb-3">Tenure at Cancellation <span className="text-uf-text-muted text-xs font-normal">(click to drill down)</span></h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(data.tenure_breakdown || {}).map(([bucket, count]) => (
            <div key={bucket} className="bg-uf-surface-hi rounded-lg p-3 text-center cursor-pointer hover:ring-1 hover:ring-uf-red transition-all"
              onClick={() => setDrillDown({ type: 'tenure', value: bucket, label: bucket.replace(/_/g, ' ') })}>
              <p className="text-2xl font-bold text-uf-text">{count}</p>
              <p className="text-xs text-uf-text-muted capitalize">{bucket.replace(/_/g, ' ')}</p>
              <ChevronRight className="w-3 h-3 text-uf-text-muted mx-auto mt-1" />
            </div>
          ))}
        </div>
      </div>
      {/* Monthly Trend */}
      {data.monthly_trend?.length > 0 && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
          <h3 className="text-sm font-semibold text-uf-text mb-3">Monthly Trend</h3>
          <div className="flex items-end gap-2 h-32">
            {data.monthly_trend.map((m,i) => {
              const max = Math.max(...data.monthly_trend.map(t => t.count), 1);
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end">
                  <div className="w-full bg-red-500/60 rounded-t transition-all" style={{height: `${Math.max((m.count/max)*100, m.count ? 8 : 0)}%`}} />
                  <span className="text-[9px] text-uf-text-muted mt-1">{m.month?.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== FAILED PAYMENTS (with drill-down) ====================
const FailedPaymentsReport = ({ data, drillDown, setDrillDown }) => {
  if (!data) return <Empty />;

  if (drillDown?.type === 'failure_reason') {
    const records = (data.detail_records || []).filter(r => r.failure_reason === drillDown.value);
    return <DrillTable title={`Failed: ${drillDown.value}`} records={records} columns={['member_name','email','amount','failure_reason','collection_date','status']} />;
  }

  return (
    <div className="space-y-4" data-testid="failed-payments-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Collections" value={data.total_collections} color="text-uf-text" />
        <Stat label="Failed" value={data.failed_count} color="text-red-400" />
        <Stat label="Failure Rate" value={`${data.failure_rate}%`} color={data.failure_rate > 10 ? 'text-red-400' : 'text-emerald-400'} />
        <Stat label="Recovery Rate" value={`${data.recovery_rate}%`} color="text-blue-400" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
          <h3 className="text-sm font-semibold text-uf-text mb-3">Failure Reasons <span className="text-uf-text-muted text-xs font-normal">(click to drill down)</span></h3>
          <div className="space-y-2">
            {Object.entries(data.reason_breakdown || {}).sort((a,b) => b[1]-a[1]).map(([reason, count]) => (
              <div key={reason} className="flex justify-between items-center p-2 bg-uf-surface-hi rounded-lg cursor-pointer hover:ring-1 hover:ring-uf-red transition-all"
                onClick={() => setDrillDown({ type: 'failure_reason', value: reason, label: `Reason: ${reason}` })}>
                <span className="text-uf-text text-sm flex items-center gap-1">{reason} <ChevronRight className="w-3 h-3 text-uf-text-muted" /></span>
                <span className="text-red-400 font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
          <h3 className="text-sm font-semibold text-uf-text mb-3">Amount Summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between"><span className="text-uf-text-muted">Collected</span><span className="text-emerald-400 font-bold text-xl">£{(data.collected_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-uf-text-muted">Failed Amount</span><span className="text-red-400 font-bold text-xl">£{(data.failed_amount || 0).toFixed(2)}</span></div>
          </div>
        </div>
      </div>
      {data.daily_trend?.length > 0 && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
          <h3 className="text-sm font-semibold text-uf-text mb-3">Daily Failure Trend</h3>
          <div className="flex items-end gap-1 h-28">
            {data.daily_trend.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.failed}/${d.total}`}>
                <div className="w-full bg-red-500/60 rounded-t" style={{ height: `${Math.max(d.rate * 2, d.failed ? 4 : 0)}px` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[9px] text-uf-text-muted">
            <span>{data.daily_trend[0]?.date}</span><span>{data.daily_trend.at(-1)?.date}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DAY PASSES (with drill-down) ====================
const DayPassReport = ({ data, drillDown, setDrillDown }) => {
  if (!data) return <Empty />;

  if (drillDown?.type === 'site_passes') {
    const records = (data.detail_records || []).filter(r => r.site_name === drillDown.value);
    return <DrillTable title={`Day Passes: ${drillDown.value}`} records={records} columns={['visitor_name','email','phone','visit_date','payment_status','created_at']} />;
  }

  return (
    <div className="space-y-4" data-testid="day-pass-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Day Passes" value={data.total_day_passes} color="text-uf-text" />
        <Stat label="Converted" value={data.conversions} color="text-emerald-400" />
        <Stat label="Conversion Rate" value={`${data.conversion_rate}%`} color="text-blue-400" />
        <Stat label="Revenue" value={`£${(data.total_revenue || 0).toFixed(2)}`} color="text-emerald-400" />
      </div>
      <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
        <div className="px-5 py-3 border-b border-uf-border">
          <h3 className="text-sm font-semibold text-uf-text">By Site <span className="text-uf-text-muted text-xs font-normal">(click to see visitors)</span></h3>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="bg-uf-surface-hi text-left text-uf-text-muted">
            <th className="px-5 py-2">Site</th><th className="px-5 py-2 text-right">Passes</th><th className="px-5 py-2 text-right">Converted</th><th className="px-5 py-2 text-right">Revenue</th>
          </tr></thead>
          <tbody>{(data.by_site || []).map(s => (
            <tr key={s.site_id} className="border-t border-uf-border/50 hover:bg-uf-surface-hi cursor-pointer"
              onClick={() => setDrillDown({ type: 'site_passes', value: s.site_name, label: s.site_name })}>
              <td className="px-5 py-2 text-uf-text font-medium flex items-center gap-1">{s.site_name} <ChevronRight className="w-3 h-3 text-uf-text-muted" /></td>
              <td className="px-5 py-2 text-right text-uf-text">{s.total}</td>
              <td className="px-5 py-2 text-right text-emerald-400">{s.converted}</td>
              <td className="px-5 py-2 text-right text-uf-text">£{s.revenue?.toFixed(2)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== AUDIT LOG ====================
const AuditLogReport = ({ logs, page, setPage }) => {
  const actionColor = (t) => { const m = { member_created: 'emerald', payment_failed: 'red', subscription_cancelled: 'red', mandate_created: 'blue', login: 'cyan' }; return m[t] || 'gray'; };
  return (
    <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="audit-report">
      <table className="w-full text-sm">
        <thead><tr className="bg-uf-surface-hi text-left text-uf-text-muted border-b border-uf-border">
          <th className="p-3">Time</th><th className="p-3">Actor</th><th className="p-3">Action</th><th className="p-3">Entity</th><th className="p-3">Details</th>
        </tr></thead>
        <tbody>{logs.map(l => (
          <tr key={l.id} className="border-b border-uf-border/50 hover:bg-uf-surface-hi">
            <td className="p-3 text-uf-text-muted text-xs">{new Date(l.timestamp).toLocaleString()}</td>
            <td className="p-3"><p className="text-uf-text text-xs">{l.actor_name || l.actor_id?.slice(0,8)}</p></td>
            <td className="p-3"><span className={`px-1.5 py-0.5 rounded text-[10px] bg-${actionColor(l.action_type)}-500/20 text-${actionColor(l.action_type)}-400`}>{l.action_type}</span></td>
            <td className="p-3 text-uf-text text-xs">{l.entity_type}</td>
            <td className="p-3 text-uf-text-muted text-xs max-w-xs truncate">{l.details || '-'}</td>
          </tr>
        ))}</tbody>
      </table>
      <div className="flex justify-center gap-2 p-3 border-t border-uf-border">
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1 bg-uf-surface-hi rounded text-sm text-uf-text disabled:opacity-40">Prev</button>
        <span className="text-uf-text-muted text-sm px-2 py-1">Page {page}</span>
        <button onClick={() => setPage(page + 1)} className="px-3 py-1 bg-uf-surface-hi rounded text-sm text-uf-text">Next</button>
      </div>
    </div>
  );
};

// ==================== DRILL-DOWN TABLE ====================
const DrillTable = ({ title, records, columns }) => {
  const colLabels = { member_name: 'Member', email: 'Email', plan_name: 'Plan', tenure_days: 'Tenure (days)', cancelled_at: 'Date', reason: 'Reason', failure_reason: 'Reason', amount: 'Amount', collection_date: 'Collection Date', status: 'Status', visitor_name: 'Visitor', phone: 'Phone', visit_date: 'Visit Date', payment_status: 'Payment', created_at: 'Created' };
  return (
    <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="drill-down-table">
      <div className="px-5 py-3 border-b border-uf-border">
        <h3 className="text-sm font-semibold text-uf-text">{title} ({records.length} records)</h3>
      </div>
      {records.length ? (
        <table className="w-full text-sm">
          <thead><tr className="bg-uf-surface-hi text-left text-uf-text-muted">
            {columns.map(c => <th key={c} className="px-5 py-2">{colLabels[c] || c}</th>)}
          </tr></thead>
          <tbody>{records.map((r, i) => (
            <tr key={i} className="border-t border-uf-border/50 hover:bg-uf-surface-hi">
              {columns.map(c => <td key={c} className="px-5 py-2 text-uf-text text-xs">{c === 'amount' ? `£${r[c]?.toFixed?.(2) || r[c]}` : (r[c] || '-')}</td>)}
            </tr>
          ))}</tbody>
        </table>
      ) : (
        <p className="p-8 text-center text-uf-text-muted text-sm">No detailed records available for this drill-down. Records will show when there is actual data.</p>
      )}
    </div>
  );
};

// ==================== SHARED ====================
const Stat = ({ label, value, color, small }) => (
  <div className="bg-uf-surface rounded-xl border border-uf-border p-4">
    <p className="text-uf-text-muted text-xs uppercase tracking-wider">{label}</p>
    <p className={`${small ? 'text-base' : 'text-2xl'} font-bold ${color} mt-1`}>{value ?? 0}</p>
  </div>
);

const Empty = () => <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center"><BarChart3 className="w-12 h-12 mx-auto mb-4 text-uf-text-muted opacity-50" /><p className="text-uf-text-muted">No data available</p></div>;

export default AdminReports;
